/*
  <kg-collection>: laedt weitere Produkte nach, sobald das Ende in Sicht kommt, und
  wechselt die Sortierung ohne Neuladen. Beides ueber die Section Rendering API, also
  dieselbe Seite, nur als Ausschnitt. Ohne JavaScript bleiben Seitenlinks und
  Absende-Knopf im Markup, die Seite arbeitet dann mit gewoehnlichen Seitenwechseln.
*/

class KgCollection extends HTMLElement {
  connectedCallback() {
    this.gitter = this.querySelector('[data-collection-grid]');
    this.mehr = this.querySelector('[data-collection-more]');
    this.sortierung = this.querySelector('[data-collection-sort] select');
    if (!this.gitter) return;

    this.sortierung?.addEventListener('change', () => this.sortieren());
    this.beobachten();
  }

  disconnectedCallback() {
    this.beobachter?.disconnect();
  }

  /* Der Knopf bleibt sichtbar, dient aber nur noch als Ziel und als Ausweg ohne Skript */
  beobachten() {
    this.beobachter?.disconnect();
    const ziel = this.querySelector('[data-collection-next]');
    if (!ziel) return;
    this.beobachter = new IntersectionObserver(
      (eintraege) => {
        if (eintraege.some((e) => e.isIntersecting)) this.nachladen();
      },
      { rootMargin: '600px 0px' }
    );
    this.beobachter.observe(ziel);
  }

  async holen(adresse) {
    const url = new URL(adresse, window.location.origin);
    /*
      Normalerweise wird nur der Ausschnitt der Section geholt, das ist ein Zehntel der
      Datenmenge. Auf der Suchseite geht das nicht: Dort antwortet die Section-Schnittstelle
      mit 404, sobald „page“ und „type“ zusammen in der Adresse stehen — und genau so sieht
      die naechste Seite dort aus. Mit data-full-page wird deshalb die ganze Seite geholt
      und das Raster daraus entnommen.
    */
    if (!this.hasAttribute('data-full-page')) {
      url.searchParams.set('section_id', this.dataset.section);
    }
    const antwort = await fetch(url, { headers: { Accept: 'text/html' } });
    if (!antwort.ok) throw new Error(String(antwort.status));
    const text = await antwort.text();
    return new DOMParser().parseFromString(text, 'text/html');
  }

  async nachladen() {
    const ziel = this.querySelector('[data-collection-next]');
    if (this.laeuft || !ziel) return;
    this.laeuft = true;
    this.beobachter?.disconnect();
    ziel.setAttribute('aria-busy', 'true');
    try {
      const doc = await this.holen(ziel.href);
      const neue = doc.querySelector('[data-collection-grid]');
      if (neue) this.gitter.append(...neue.children);
      const naechste = doc.querySelector('[data-collection-more]');
      if (naechste) this.mehr.replaceWith(naechste);
      else this.mehr?.remove();
      this.mehr = this.querySelector('[data-collection-more]');
    } catch {
      ziel.removeAttribute('aria-busy');
    } finally {
      this.laeuft = false;
      this.beobachten();
    }
  }

  async sortieren() {
    if (this.laeuft) return;
    this.laeuft = true;
    this.setAttribute('aria-busy', 'true');
    const adresse = new URL(window.location.href);
    adresse.searchParams.set('sort_by', this.sortierung.value);
    adresse.searchParams.delete('page');
    try {
      const doc = await this.holen(adresse);
      const neue = doc.querySelector('[data-collection-grid]');
      if (neue) this.gitter.replaceChildren(...neue.children);
      const naechste = doc.querySelector('[data-collection-more]');
      if (naechste && this.mehr) this.mehr.replaceWith(naechste);
      else if (naechste) this.append(naechste);
      else this.mehr?.remove();
      this.mehr = this.querySelector('[data-collection-more]');
      window.history.replaceState({}, '', adresse);
      this.gitter.scrollIntoView({ block: 'start', behavior: 'smooth' });
    } catch {
      /* Beim Fehler bleibt die alte Reihenfolge stehen */
    } finally {
      this.laeuft = false;
      this.removeAttribute('aria-busy');
      this.beobachten();
    }
  }
}

customElements.define('kg-collection', KgCollection);
